
import { useNavigate } from 'react-router-dom'
import { 
  BarChart3, 
  Target, 
  FileText, 
  Activity,
  Plus,
  ArrowRight
} from 'lucide-react'
import { 
  mockStrategies, 
  mockManualTestTrades, 
  mockBacktestResults,
  mockLiveTracking,
  mockNotes,
  mockManualTestStats 
} from '../utils/mockData'
import Navigation from '../components/Navigation'

export default function TestPage() {
  const navigate = useNavigate()


  return (
    <div className="min-h-screen p-8">
      <Navigation />
      <div className="max-w-6xl mx-auto space-y-8">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">TideLab Mock数据测试</h1>
          <p className="text-white/70">验证所有功能模块的mock数据展示</p>
        </div>

        {/* 策略列表 */}
        <div className="card">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <BarChart3 className="w-6 h-6 mr-2" />
            策略列表 ({mockStrategies.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockStrategies.map((strategy) => (
              <div key={strategy.id} className="card hover:scale-105 transition-all duration-300">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-2">{strategy.name}</h3>
                    <p className="text-sm text-white/70 mb-3">{strategy.description}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    strategy.status === 'live' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                    strategy.status === 'test' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                    'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                  }`}>
                    {strategy.status === 'live' ? '实盘中' :
                     strategy.status === 'test' ? '验证中' : '草稿'}
                  </span>
                </div>
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-white/60">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    {strategy.market_symbol} • {strategy.timeframe}
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button 
                    onClick={() => navigate(`/strategy/${strategy.id}`)}
                    className="flex-1 btn-secondary text-sm"
                  >
                    查看详情
                  </button>
                  <button className="flex-1 btn-primary text-sm">
                    运行回测
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 手动测试数据 */}
        <div className="card">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <Target className="w-6 h-6 mr-2" />
            手动测试交易记录 ({mockManualTestTrades.length})
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="text-left p-3">策略</th>
                  <th className="text-left p-3">方向</th>
                  <th className="text-left p-3">入场价格</th>
                  <th className="text-left p-3">出场价格</th>
                  <th className="text-left p-3">盈亏</th>
                  <th className="text-left p-3">R值</th>
                </tr>
              </thead>
              <tbody>
                {mockManualTestTrades.map((trade) => (
                  <tr key={trade.id} className="border-b border-white/10">
                    <td className="p-3">{mockStrategies.find(s => s.id === trade.strategy_id)?.name}</td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded text-xs ${
                        trade.direction === 'long' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        {trade.direction === 'long' ? '做多' : '做空'}
                      </span>
                    </td>
                    <td className="p-3">${trade.entry_price.toLocaleString()}</td>
                    <td className="p-3">${trade.exit_price.toLocaleString()}</td>
                    <td className={`p-3 font-semibold ${
                      trade.pnl_amount > 0 ? 'text-green-400' : 'text-red-400'
                    }`}>
                      ${trade.pnl_amount.toLocaleString()}
                    </td>
                    <td className="p-3">{trade.pnl_r.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* 统计数据 */}
        <div className="card">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <BarChart3 className="w-6 h-6 mr-2" />
            手动测试统计数据
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-white">{mockManualTestStats.total_trades}</div>
              <div className="text-sm text-white/70">总交易数</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">{(mockManualTestStats.win_rate * 100).toFixed(1)}%</div>
              <div className="text-sm text-white/70">胜率</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">${mockManualTestStats.total_pnl.toLocaleString()}</div>
              <div className="text-sm text-white/70">总盈亏</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">{mockManualTestStats.avg_r_value.toFixed(2)}</div>
              <div className="text-sm text-white/70">平均R值</div>
            </div>
          </div>
        </div>

        {/* 回测结果 */}
        <div className="card">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <BarChart3 className="w-6 h-6 mr-2" />
            回测结果 ({mockBacktestResults.length})
          </h2>
          <div className="space-y-4">
            {mockBacktestResults.map((backtest) => (
              <div key={backtest.id} className="border border-white/20 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-white">
                    {mockStrategies.find(s => s.id === backtest.strategy_id)?.name}
                  </h3>
                  <span className="text-sm text-white/60">
                    {new Date(backtest.start_date).toLocaleDateString()} - {new Date(backtest.end_date).toLocaleDateString()}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="text-white/60">总收益率</div>
                    <div className="text-white font-semibold">{backtest.total_return.toFixed(2)}%</div>
                  </div>
                  <div>
                    <div className="text-white/60">最大回撤</div>
                    <div className="text-white font-semibold">{backtest.max_drawdown.toFixed(2)}%</div>
                  </div>
                  <div>
                    <div className="text-white/60">胜率</div>
                    <div className="text-white font-semibold">{(backtest.win_rate * 100).toFixed(1)}%</div>
                  </div>
                  <div>
                    <div className="text-white/60">交易次数</div>
                    <div className="text-white font-semibold">{backtest.trade_count}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 实盘追踪 */}
        <div className="card">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <Activity className="w-6 h-6 mr-2" />
            实盘追踪 ({mockLiveTracking.length})
          </h2>
          <div className="space-y-4">
            {mockLiveTracking.map((tracking) => (
              <div key={tracking.id} className="border border-white/20 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-white">
                    {mockStrategies.find(s => s.id === tracking.strategy_id)?.name}
                  </h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    tracking.status === 'active' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                    'bg-gray-500/20 text-gray-400 border border-gray-500/30'
                  }`}>
                    {tracking.status === 'active' ? '进行中' : '已完成'}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="text-white/60">实际收益率</div>
                    <div className="text-white font-semibold">{tracking.actual_return?.toFixed(2)}%</div>
                  </div>
                  <div>
                    <div className="text-white/60">实际回撤</div>
                    <div className="text-white font-semibold">{tracking.actual_drawdown?.toFixed(2)}%</div>
                  </div>
                  <div>
                    <div className="text-white/60">交易次数</div>
                    <div className="text-white font-semibold">{tracking.actual_trade_count}</div>
                  </div>
                  <div>
                    <div className="text-white/60">平台</div>
                    <div className="text-white font-semibold">{tracking.platform}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 研究笔记 */}
        <div className="card">
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
            <FileText className="w-6 h-6 mr-2" />
            研究笔记 ({mockNotes.length})
          </h2>
          <div className="space-y-4">
            {mockNotes.map((note) => (
              <div key={note.id} className="border border-white/20 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-lg font-semibold text-white">{note.title}</h3>
                  <span className="text-sm text-white/60">
                    {new Date(note.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-white/80">{note.content}</p>
                <div className="mt-2">
                  <span className="text-xs text-white/60">
                    策略: {mockStrategies.find(s => s.id === note.strategy_id)?.name}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 导航按钮 */}
        <div className="card text-center">
          <h2 className="text-2xl font-bold text-white mb-6">功能导航</h2>
          <div className="flex flex-wrap justify-center gap-4">
            <button 
              onClick={() => navigate('/')}
              className="btn-primary flex items-center"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              返回首页
            </button>
            <button 
              onClick={() => navigate('/strategy/new')}
              className="btn-primary flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              新建策略
            </button>
            <button 
              onClick={() => navigate('/button-test')}
              className="btn-secondary flex items-center"
            >
              按钮测试
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
} 